<p-card>
  <ng-template pTemplate="header">
    <app-data-pad-card-header
      [showEdit]="false"
      [showDataPad]="false"
      [showClose]="true"
      (closeClick)="closeDataPad()">
      <div class="p-card-title">
        {{ isCreateMode ? 'New Blog Post' : 'Edit Blog Post' }}
      </div>
    </app-data-pad-card-header>
  </ng-template>

  <form [formGroup]="form" class="flex flex-col gap-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="flex flex-col gap-1">
        <label>Title</label>
        <input pInputText formControlName="title" class="w-full" />
      </div>
      <div class="flex flex-col gap-1">
        <label>Slug</label>
        <input pInputText formControlName="slug" class="w-full" placeholder="auto-generated if empty" />
      </div>
      <div class="flex flex-col gap-1 md:col-span-2">
        <label>Author</label>
        <input pInputText formControlName="author" class="w-full" />
      </div>
      <div class="flex flex-col gap-1 md:col-span-2">
        <div class="flex items-center justify-between gap-2">
          <label>Series</label>
          <div class="flex gap-2">
            <p-button label="Add Series" size="small" severity="secondary" (onClick)="openAddSeriesDialog()" />
            <p-button label="Manage Series" size="small" severity="secondary" [outlined]="true"
              (onClick)="manageSeries()" />
          </div>
        </div>
        <p-select formControlName="seriesId" [options]="seriesOptions()" optionLabel="name" optionValue="id"
          [showClear]="true" placeholder="Select series" class="w-full" />
      </div>
      <div class="flex flex-col gap-1 md:col-span-2">
        <div class="flex items-center justify-between gap-2">
          <label>Tags</label>
          <div class="flex gap-2">
            <p-button label="Add Tag" size="small" severity="secondary" (onClick)="openAddTagDialog()" />
            <p-button label="Manage Tags" size="small" severity="secondary" [outlined]="true"
              (onClick)="manageTags()" />
          </div>
        </div>
        <p-multiSelect formControlName="tagIds" [options]="tagOptions()" optionLabel="name" optionValue="id"
          [filter]="true" placeholder="Select tags" class="w-full" />
      </div>
      <div class="flex flex-col gap-1 md:col-span-2">
        <label>Excerpt</label>
        <textarea pTextarea formControlName="excerpt" rows="3" class="w-full"></textarea>
      </div>
      <div class="flex flex-col gap-2 md:col-span-2">
        <label>Hero Image (optional, 16:9)</label>
        <input #heroImageInput type="file" accept="image/*" class="hidden"
          (change)="onHeroImageSelected($event)" />

        @if (!croppingHero()) {
          <div class="flex flex-wrap gap-2">
            <p-button label="Choose Hero Image" icon="pi pi-image" severity="secondary"
              (onClick)="heroImageInput.click()" />
            @if (heroImageUrl()) {
              <p-button label="Remove" severity="danger" [outlined]="true" (onClick)="clearHeroImage()" />
            }
          </div>
        }

        @if (croppingHero()) {
          <div class="flex flex-col gap-3 rounded border border-surface-200 p-3">
            <p class="text-sm text-surface-600 m-0">
              Crop to 16:9 so the image matches the live blog hero.
            </p>
            <image-cropper
              class="w-full"
              [aspectRatio]="heroAspectRatio"
              [maintainAspectRatio]="true"
              [resizeToWidth]="1600"
              [imageChangedEvent]="heroImageChangedEvent"
              [transform]="imageTransform()"
              [allowMoveImage]="true"
              format="jpeg"
              [imageQuality]="92"
              (imageCropped)="onHeroImageCropped($event)" />
            <div
              class="hero-rotate-controls flex flex-col items-stretch gap-2 w-full"
              (pointerdown)="$event.stopPropagation()"
              (mousedown)="$event.stopPropagation()">
              <div class="flex items-center gap-2">
                <p-button
                  label="-15°"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  (onClick)="nudgeRotate(-15)" />
                <input
                  class="hero-rotate-range flex-1"
                  type="range"
                  min="-180"
                  max="180"
                  step="1"
                  [value]="rotateValue()"
                  (input)="onRotateInput($event)" />
                <p-button
                  label="+15°"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  (onClick)="nudgeRotate(15)" />
              </div>
              <span class="text-sm text-center">Rotate {{ rotateValue() }}°</span>
            </div>
            <div class="flex justify-end gap-2">
              <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="cancelHeroCrop()" />
              <p-button label="Confirm Crop" (onClick)="confirmHeroCrop()" />
            </div>
          </div>
        } @else if (heroImageUrl()) {
          <div class="flex flex-col gap-4 mt-1">
            <div>
              <div class="text-sm font-medium mb-2">Post page preview</div>
              <div class="hero-preview-post overflow-hidden rounded-lg">
                <img [src]="heroImageUrl()" alt="Post hero preview" class="w-full h-auto block" />
              </div>
            </div>
            <div>
              <div class="text-sm font-medium mb-2">Archive thumbnail preview</div>
              <div class="hero-preview-archive overflow-hidden rounded-md">
                <img [src]="heroImageUrl()" alt="Archive hero preview" class="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </form>

  <div class="mt-6">
    <div class="flex flex-wrap gap-2 mb-4">
      <p-button label="Add Text" (onClick)="addBlock('rich_text')" size="small" />
      <p-button label="Add Image" (onClick)="addBlock('image')" size="small" />
      <p-button label="Add Video" (onClick)="addBlock('video')" size="small" />
      <p-button label="Add CTA" (onClick)="addBlock('cta')" size="small" />
    </div>

    @for (block of blocks(); track $index; let i = $index) {
      <p-card class="mb-4">
        <div class="flex justify-between items-center mb-3">
          <span class="font-semibold uppercase text-sm">{{ block.blockType }}</span>
          <div class="flex gap-2">
            <app-icon-button [icon]="icons.solid.faArrowUp" (onClick)="moveBlock(i, -1)"
              pTooltip="Move Block Up" tooltipPosition="top" [showDelay]="300" />
            <app-icon-button [icon]="icons.solid.faArrowDown" (onClick)="moveBlock(i, 1)"
              pTooltip="Move Block Down" tooltipPosition="top" [showDelay]="300" />
            <app-icon-button [icon]="icons.ppmSolid.delete" (onClick)="removeBlock(i)"
              pTooltip="Delete Block" tooltipPosition="top" [showDelay]="300" />
          </div>
        </div>

        @switch (block.blockType) {
          @case ('rich_text') {
            <app-prose-mirror editorType="main-site-content"
              [ngModel]="asRichText(block.content).html"
              (ngModelChange)="updateRichText(i, $event)" />
          }
          @case ('image') {
            <div class="flex flex-col gap-2">
              <input #blockImageInput type="file" accept="image/*" class="hidden"
                (change)="onImageFileSelected(i, $event)" />
              <p-button label="Choose Image" icon="pi pi-image" severity="secondary"
                (onClick)="blockImageInput.click()" />
              @if (asImage(block.content).url) {
                <img [src]="asImage(block.content).url" alt="" class="max-h-48 rounded" />
              }
              <input pInputText placeholder="Alt text" [value]="asImage(block.content).alt ?? ''"
                (input)="updateImageField(i, 'alt', $any($event.target).value)" />
              <input pInputText placeholder="Caption" [value]="asImage(block.content).caption ?? ''"
                (input)="updateImageField(i, 'caption', $any($event.target).value)" />
            </div>
          }
          @case ('video') {
            <div class="flex flex-col gap-2">
              <input pInputText placeholder="YouTube URL" [value]="getVideoUrl(i)"
                (input)="updateVideoUrl(i, $any($event.target).value)" />
              @if (asVideo(block.content).youtubeId) {
                <app-youtube-embed [youtubeVideoId]="asVideo(block.content).youtubeId" />
              }
            </div>
          }
          @case ('cta') {
            <div class="flex flex-col gap-2">
              <input pInputText placeholder="Button label" [value]="asCta(block.content).label"
                (input)="updateCtaField(i, 'label', $any($event.target).value)" />
              <input pInputText placeholder="URL" [value]="asCta(block.content).url"
                (input)="updateCtaField(i, 'url', $any($event.target).value)" />
              <label class="flex items-center gap-2">
                <p-checkbox [binary]="true" [ngModel]="asCta(block.content).openInNewTab ?? true"
                  (ngModelChange)="updateCtaField(i, 'openInNewTab', $event)" />
                Open in new tab
              </label>
            </div>
          }
        }
      </p-card>
    }
  </div>

  <div class="flex gap-2 mt-4">
    <app-save-button (saveClick)="save()" />
    @if (editPost?.slug || form.controls.slug.value) {
      <p-button label="Preview on Staging" severity="secondary" [outlined]="true" (onClick)="previewOnStaging()" />
    }
    @if (isCreateMode || editPost?.status !== 'published') {
      <p-button label="Publish & Email" severity="success" (onClick)="publish()" />
    } @else {
      <p-button label="Update Live Site" severity="info" (onClick)="publish()" />
    }
  </div>
</p-card>

<p-dialog
  [visible]="showAddTagDialog()"
  (visibleChange)="showAddTagDialog.set($event)"
  header="Add Tag"
  [modal]="true"
  [style]="{ width: '28rem' }">
  <form [formGroup]="addTagForm" class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label>Name</label>
      <input pInputText formControlName="name" class="w-full" />
    </div>
    <div class="flex flex-col gap-1">
      <label>Slug</label>
      <input pInputText formControlName="slug" class="w-full" placeholder="auto-generated if empty" />
    </div>
  </form>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" (onClick)="showAddTagDialog.set(false)" />
    <p-button label="Add Tag" (onClick)="createTag()" />
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="showAddSeriesDialog()"
  (visibleChange)="showAddSeriesDialog.set($event)"
  header="Add Series"
  [modal]="true"
  [style]="{ width: '28rem' }">
  <form [formGroup]="addSeriesForm" class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label>Name</label>
      <input pInputText formControlName="name" class="w-full" />
    </div>
    <div class="flex flex-col gap-1">
      <label>Slug</label>
      <input pInputText formControlName="slug" class="w-full" placeholder="auto-generated if empty" />
    </div>
  </form>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" (onClick)="showAddSeriesDialog.set(false)" />
    <p-button label="Add Series" (onClick)="createSeries()" />
  </ng-template>
</p-dialog>
